// ═══════════════════════════════════════════════════════════════════════
// Iter. 411.mm8 — Custom rest-timer sounds (synthesized via Web Audio API).
//
// Why synthesized instead of MP3/OGG assets?
//   • Zero bundle-weight impact (each pack is ~20 lines of code vs 30-80KB
//     of audio per file)
//   • Same low-latency start on every device (no network fetch before beep)
//   • Consistent volume normalisation
//
// Every pack exports two functions: `warning()` (fires at T-3s) and `end()`
// (fires at T=0). All are non-blocking and safe to call from any handler.
// ═══════════════════════════════════════════════════════════════════════

function _makeContext() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  const ctx = new AC();
  if (ctx.state === 'suspended' && ctx.resume) ctx.resume().catch(() => {});
  return ctx;
}

// ── Global volume multiplier (0..1) ───────────────────────────────────
// Kept as module state so:
//   • The Settings slider drives previews instantly, and
//   • The <RestTimer> in-workout reads the same value hydrated from the
//     user profile on mount without having to thread props through the
//     tone helpers.
// Clamped to [0.0, 1.0]. 0 is truly silent (we short-circuit _tone).
let _volume = 1.0;
export function setSoundVolume(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return;
  _volume = Math.max(0, Math.min(1, n));
}
export function getSoundVolume() {
  return _volume;
}

function _tone(ctx, { freq, duration, volume = 0.5, type = 'sine', attack = 0.01, decay = 0.1, when = 0 }) {
  const scaled = volume * _volume;
  if (scaled <= 0.0005) return null; // muted — save the oscillator allocation
  const t0 = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  gain.gain.setValueAtTime(0.0001, t0);
  gain.gain.exponentialRampToValueAtTime(scaled, t0 + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(t0);
  osc.stop(t0 + duration + 0.05);
  return { osc, gain };
}

// ── DEFAULT beep pack (kept identical to legacy behaviour) ────────────
const beep = {
  warning() {
    const ctx = _makeContext(); if (!ctx) return;
    _tone(ctx, { freq: 600, duration: 0.12, volume: 0.3, type: 'sine', attack: 0.005 });
    setTimeout(() => ctx.close().catch(() => {}), 250);
  },
  end() {
    const ctx = _makeContext(); if (!ctx) return;
    _tone(ctx, { freq: 1000, duration: 0.2,  volume: 0.6, type: 'sine' });
    _tone(ctx, { freq: 1200, duration: 0.2,  volume: 0.6, type: 'sine', when: 0.25 });
    _tone(ctx, { freq: 1400, duration: 0.3,  volume: 0.7, type: 'sine', when: 0.5 });
    setTimeout(() => ctx.close().catch(() => {}), 1000);
  },
};

// ── KLAXON pack (air horn / two-tone car horn) ────────────────────────
const klaxon = {
  warning() {
    const ctx = _makeContext(); if (!ctx) return;
    _tone(ctx, { freq: 320, duration: 0.15, volume: 0.35, type: 'square', attack: 0.005 });
    setTimeout(() => ctx.close().catch(() => {}), 300);
  },
  end() {
    const ctx = _makeContext(); if (!ctx) return;
    // Truck / air-horn — two dissonant square waves stacked for that
    // characteristic "HONK". Repeat twice for the classic HONK-HONK.
    for (let i = 0; i < 2; i++) {
      const start = i * 0.55;
      _tone(ctx, { freq: 380, duration: 0.42, volume: 0.55, type: 'square', attack: 0.02, when: start });
      _tone(ctx, { freq: 440, duration: 0.42, volume: 0.45, type: 'sawtooth', attack: 0.02, when: start });
    }
    setTimeout(() => ctx.close().catch(() => {}), 1600);
  },
};

// ── GONG pack (deep resonant temple gong) ─────────────────────────────
const gong = {
  warning() {
    const ctx = _makeContext(); if (!ctx) return;
    _tone(ctx, { freq: 200, duration: 0.35, volume: 0.4, type: 'triangle', attack: 0.01, decay: 0.3 });
    setTimeout(() => ctx.close().catch(() => {}), 600);
  },
  end() {
    const ctx = _makeContext(); if (!ctx) return;
    // Layer fundamental + 2 harmonics with different decays for a rich
    // metallic sustain. Total duration ~2.5s.
    _tone(ctx, { freq: 65,  duration: 2.5, volume: 0.7, type: 'sine',     attack: 0.02 });
    _tone(ctx, { freq: 130, duration: 2.0, volume: 0.4, type: 'sine',     attack: 0.03 });
    _tone(ctx, { freq: 195, duration: 1.5, volume: 0.25, type: 'triangle', attack: 0.05 });
    // Metallic shimmer at higher harmonic
    _tone(ctx, { freq: 520, duration: 0.8, volume: 0.15, type: 'triangle', attack: 0.02 });
    setTimeout(() => ctx.close().catch(() => {}), 2800);
  },
};

// ── BOXING BELL pack ──────────────────────────────────────────────────
const boxing_bell = {
  warning() {
    const ctx = _makeContext(); if (!ctx) return;
    _tone(ctx, { freq: 700, duration: 0.15, volume: 0.35, type: 'triangle', attack: 0.003 });
    setTimeout(() => ctx.close().catch(() => {}), 300);
  },
  end() {
    const ctx = _makeContext(); if (!ctx) return;
    // Classic ringside bell — sharp attack, bright ping, ~1.5s decay.
    // 3 strikes: ring, ring, ring (ding-ding-ding).
    for (let i = 0; i < 3; i++) {
      const when = i * 0.45;
      _tone(ctx, { freq: 850,  duration: 1.5, volume: 0.55, type: 'triangle', attack: 0.002, when });
      _tone(ctx, { freq: 1700, duration: 1.2, volume: 0.30, type: 'sine',     attack: 0.002, when });
      _tone(ctx, { freq: 2550, duration: 0.7, volume: 0.15, type: 'sine',     attack: 0.002, when });
    }
    setTimeout(() => ctx.close().catch(() => {}), 2500);
  },
};

const PACKS = { beep, klaxon, gong, boxing_bell };

export function getSoundPack(id) {
  return PACKS[id] || PACKS.beep;
}

export function previewSound(id) {
  getSoundPack(id).end();
}

export const SOUND_OPTIONS = [
  { id: 'beep',        label_fr: 'Bip classique',   label_en: 'Classic beep',  label_es: 'Bip clásico',   emoji: '🔔' },
  { id: 'klaxon',      label_fr: 'Klaxon',          label_en: 'Air horn',      label_es: 'Bocina',        emoji: '📣' },
  { id: 'gong',        label_fr: 'Gong',            label_en: 'Gong',          label_es: 'Gong',          emoji: '🥁' },
  { id: 'boxing_bell', label_fr: 'Cloche de boxe',  label_en: 'Boxing bell',   label_es: 'Campana boxeo', emoji: '🥊' },
];
